import { Box, Button, Fade, Paper, Popper, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';

import { getViewerOverlaySurfaceStyles } from 'components/theme/theme';
import { ANALYSIS_AREAS_EXPLORE_PARAM } from './analysisAreasExplore';

const EXIT_MS = 180;

export const analysisAreasExploreGuidance = {
  dataset:
    'Open a slide to draw Include within tissue or Exclude areas. The tissue mask opens at 30% so you can see the area to keep. Without Include, the tissue mask is used. Exclude areas are then removed.',
  viewer:
    'Draw Include within tissue or Exclude areas. The tissue mask is open at 30% so you can see the area to keep. Without Include, the tissue mask is used. Exclude areas are then removed.',
};

interface AnalysisAreasExploreGuideProps {
  variant: 'popper' | 'overlay';
  anchorEl?: HTMLElement | null;
}

export const AnalysisAreasExploreGuide: React.FC<AnalysisAreasExploreGuideProps> = ({ variant, anchorEl }) => {
  const [active] = useQueryParam(ANALYSIS_AREAS_EXPLORE_PARAM, BooleanParam);
  const [phase, setPhase] = useState<'guide' | 'exit' | 'dismissed'>('guide');

  useEffect(() => {
    if (active) {
      setPhase('guide');
    }
  }, [active]);

  useEffect(() => {
    if (phase !== 'exit') {
      return undefined;
    }
    const exit = window.setTimeout(() => setPhase('dismissed'), EXIT_MS);
    return () => window.clearTimeout(exit);
  }, [phase]);

  if (!active || phase === 'dismissed' || (variant === 'popper' && !anchorEl)) {
    return null;
  }

  const bubble = (
    <Fade in={phase !== 'exit'} timeout={EXIT_MS} unmountOnExit>
      <Paper
        role="status"
        aria-live="polite"
        aria-label="Annotate analysis areas"
        data-testid="analysis-areas-explore-guide"
        variant={variant === 'overlay' ? 'elevation' : 'outlined'}
        elevation={0}
        sx={(theme) => ({
          width: 320,
          maxWidth: 'min(320px, calc(100vw - 32px))',
          p: 1.5,
          pointerEvents: 'auto',
          ...(variant === 'overlay' ? getViewerOverlaySurfaceStyles(theme) : {}),
        })}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2">Annotate analysis areas</Typography>
          <Typography variant="body2" color="text.secondary">
            {variant === 'overlay' ? analysisAreasExploreGuidance.viewer : analysisAreasExploreGuidance.dataset}
          </Typography>
          <Box>
            <Button size="small" variant="contained" color="secondary" onClick={() => setPhase('exit')}>
              OK
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Fade>
  );

  if (variant === 'overlay') {
    return (
      <Box
        sx={{
          position: 'absolute',
          zIndex: 120,
          left: '50%',
          bottom: 72,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
      >
        {bubble}
      </Box>
    );
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom"
      sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
    >
      {bubble}
    </Popper>
  );
};
